import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { UpdoxDirectMessage, MessageFolder } from '../updox-direct-message';
import { UpdoxMessagesService } from '../updox-messages.service';

export interface FolderStats {
  folder: MessageFolder;
  total: number;
  unread: number;
}

@Component({
  selector: 'app-updox-message-list',
  templateUrl: './message-list.component.html',
})
export class UpdoxMessageListComponent implements OnChanges {
  @Input() folder: MessageFolder = 'inbox';
  @Input() practiceId: number;
  @Input() reloadTrigger: boolean;
  @Output() messageSelected = new EventEmitter<UpdoxDirectMessage>();
  @Output() countsChanged = new EventEmitter<FolderStats>();

  messages: UpdoxDirectMessage[] = [];
  selectedMessage: UpdoxDirectMessage = null;
  isLoading = false;
  isLoadingMore = false;
  currentPage = 1;
  totalPages = 1;
  totalCount = 0;
  searchTerm = '';

  constructor(
    private messagesService: UpdoxMessagesService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.folder || changes.practiceId || changes.reloadTrigger) && this.practiceId) {
      this.loadMessages();
    }
  }

  loadMessages(): void {
    this.isLoading = true;
    this.currentPage = 1;
    if (this.selectedMessage) {
      this.selectedMessage = null;
      this.messageSelected.emit(null);
    }
    this.messagesService.getMessages(this.practiceId, this.folder, 1)
      .then(({ messages, page, totalPages, totalCount }) => {
        this.isLoading = false;
        this.messages = messages.map(m => new UpdoxDirectMessage().load_from_json(m));
        this.currentPage = page;
        this.totalPages = totalPages;
        this.totalCount = totalCount;
        this.emitCounts();
      })
      .catch(() => {
        this.isLoading = false;
        this.messages = [];
        this.totalPages = 1;
        this.totalCount = 0;
      });
  }

  loadMore(): void {
    if (this.isLoadingMore || !this.hasMore) { return; }
    this.isLoadingMore = true;
    this.messagesService.getMessages(this.practiceId, this.folder, this.currentPage + 1)
      .then(({ messages, page, totalPages, totalCount }) => {
        this.isLoadingMore = false;
        const next = messages.map(m => new UpdoxDirectMessage().load_from_json(m));
        this.messages = [...this.messages, ...next];
        this.currentPage = page;
        this.totalPages = totalPages;
        this.totalCount = totalCount;
      })
      .catch(() => {
        this.isLoadingMore = false;
      });
  }

  get hasMore(): boolean {
    return this.currentPage < this.totalPages;
  }

  selectMessage(message: UpdoxDirectMessage): void {
    this.selectedMessage = message;
    this.messageSelected.emit(message);
    // Drafts are local-only — no Updox message id to mark read.
    if (!message.read && message.folder !== 'draft') {
      message.read = true;
      this.messagesService.markRead(message.id, true).catch(() => {});
    }
  }

  get filteredMessages(): UpdoxDirectMessage[] {
    if (!this.searchTerm.trim()) { return this.messages; }
    const term = this.searchTerm.toLowerCase();
    return this.messages.filter(m =>
      m.list_sender.toLowerCase().includes(term) ||
      m.display_subject.toLowerCase().includes(term) ||
      m.preview.toLowerCase().includes(term)
    );
  }

  removeMessage(message: UpdoxDirectMessage): void {
    this.messages = this.messages.filter(m => m.id !== message.id);
    this.totalCount = Math.max(0, this.totalCount - 1);
    if (this.selectedMessage?.id === message.id) {
      this.selectedMessage = null;
      this.messageSelected.emit(null);
    }
    this.emitCounts();
  }

  private emitCounts(): void {
    const unread = this.messages.filter(m => !m.read).length;
    this.countsChanged.emit({ folder: this.folder, total: this.totalCount, unread });
  }
}
